import { InjectionToken, Provider, inject } from '@angular/core';
import type { ResolveFn } from '@angular/router';
import type {
  Article,
  ArticleIndex,
  ContentEvent,
  ContentSet,
  Job,
  JobIndex,
  Person,
  Reference,
  ReferenceIndex,
} from '@flowup/content/model';
import { environment } from '../../environments/environment';

/**
 * The content of the language branch a page renders under. Each language
 * routing file provides its own set, so a Czech page never loads English
 * content and the other way round.
 */
export const CONTENT = new InjectionToken<ContentSet>('CONTENT');

export const provideContent = (content: ContentSet): Provider[] => [
  { provide: CONTENT, useValue: content },
];

/*
 * Pages get their content from route resolvers, never by loading it in the
 * component: the router waits for a resolver on the server and on the
 * client's first navigation alike, so the hydrating render starts from the
 * same data the server rendered and no transfer state is needed. Component
 * input binding hands each resolved key to the input of the same name.
 */

export const referenceIndexResolver: ResolveFn<ReferenceIndex> = () =>
  inject(CONTENT).references();

/** Only a reference with a detail page resolves; the rest are cards only. */
export const referenceResolver: ResolveFn<Reference | null> = route =>
  inject(CONTENT)
    .references()
    .then(index => index.load(route.paramMap.get('id') ?? ''))
    .then(reference => (reference?.showDetail ? reference : null));

export const articleIndexResolver: ResolveFn<ArticleIndex> = () =>
  inject(CONTENT).articles();

export const articleResolver: ResolveFn<Article | null> = route =>
  inject(CONTENT)
    .articles()
    .then(index => index.load(route.paramMap.get('id') ?? ''));

export const jobIndexResolver: ResolveFn<JobIndex> = () =>
  inject(CONTENT).jobs();

export const jobResolver: ResolveFn<Job | null> = route =>
  inject(CONTENT)
    .jobs()
    .then(index => index.load(route.paramMap.get('id') ?? ''));

export const peopleResolver: ResolveFn<Readonly<Record<string, Person>>> = () =>
  inject(CONTENT).people();

export const eventsResolver: ResolveFn<readonly ContentEvent[]> = () =>
  inject(CONTENT).events();

/**
 * The ngBeer micro-site sits outside the language branches (it is
 * English-only), so it reads the English set from the environment directly.
 */
export const ngBeerEventsResolver: ResolveFn<readonly ContentEvent[]> = () =>
  environment.content.en.events();
